import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { cn } from '@/_shared/lib/utils';

import { ScmProviderIcon } from './ScmProviderIcon';

export interface Repo {
  installationId: number;
  fullName: string;
  provider?: 'github' | 'gitlab';
  projectId?: number;
  connectionId?: string;
}

interface RepoComboboxProps {
  repos: Repo[];
  value: string;
  onChange: (repoKey: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

function toKey(repo: Repo): string {
  if (repo.provider === 'gitlab') {
    return `gitlab:${repo.connectionId}:${repo.projectId}:${repo.fullName}`;
  }
  return `${repo.installationId}:${repo.fullName}`;
}

export function RepoCombobox({
  repos,
  value,
  onChange,
  placeholder = 'Select repository...',
  disabled = false,
}: RepoComboboxProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return repos;
    const q = search.toLowerCase();
    return repos.filter((r) => r.fullName.toLowerCase().includes(q));
  }, [repos, search]);

  const selectedRepo = useMemo(() => repos.find((r) => toKey(r) === value), [repos, value]);

  const handleSelect = useCallback(
    (repo: Repo) => {
      const key = toKey(repo);
      if (key !== value) onChange(key);
      setOpen(false);
      setSearch('');
    },
    [onChange, value],
  );

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setSearch('');
  }, []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">{selectedRepo ? selectedRepo.fullName : placeholder}</span>
          <ChevronsUpDown className="ml-2 size-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 size-3.5 shrink-0 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search repositories..."
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1" role="listbox">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground" role="status">
              No repositories found.
            </p>
          ) : (
            filtered.map((repo) => {
              const key = toKey(repo);
              const selected = key === value;
              return (
                <button
                  type="button"
                  key={key}
                  role="option"
                  aria-selected={selected}
                  onClick={() => handleSelect(repo)}
                  className={cn(
                    'flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent',
                    selected && 'bg-accent',
                  )}
                >
                  <Check className={cn('mr-2 size-3.5 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
                  <ScmProviderIcon provider={repo.provider ?? 'github'} size={12} />
                  <span className="ml-1.5 truncate">{repo.fullName}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
